import React, {useMemo, useState} from 'react';
import {
    PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Moment from 'moment';
import 'moment-timezone';
import NumberFormat from 'react-number-format';

const COLORS = {
    novi: '#10b981',      // green
    vracajuci: '#3b82f6', // blue
    lojalni: '#8b5cf6',   // purple
    neaktivni: '#f59e0b'  // orange
};

/**
 * LoyaltyAnalytics component
 *
 * Uses pre-aggregated data from Firestore.
 * Shows "not available" message if no aggregated data exists.
 *
 * @param {Object} analyticsReport - Pre-aggregated report from Firestore
 */
export const LoyaltyAnalytics = ({analyticsReport}) => {
    // State for selected store
    const [selectedStore, setSelectedStore] = useState('all');

    // Check if we have aggregated data
    const hasAggregatedData = analyticsReport && analyticsReport.visits && analyticsReport.scans;

    // Get store list from scans data
    const storeList = useMemo(() => {
        if (!analyticsReport || !analyticsReport.scans || !analyticsReport.scans.storeList) {
            return [];
        }
        return analyticsReport.scans.storeList || [];
    }, [analyticsReport]);

    // Get selected store analytics
    const selectedStoreData = useMemo(() => {
        if (selectedStore === 'all' || !analyticsReport?.scans?.storeAnalytics) {
            return null;
        }
        const storeAnalytics = analyticsReport.scans.storeAnalytics[selectedStore];
        if (!storeAnalytics) return null;

        return {
            monthlyActivity: (storeAnalytics.monthlyActivity || []).map(item => ({
                ...item,
                monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
            })),
            cumulativeCustomerGrowth: (storeAnalytics.cumulativeCustomerGrowth || []).map(item => ({
                ...item,
                monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
            })),
            totalScansAllTime: storeAnalytics.totalScansAllTime || 0,
            totalScansLast12Months: storeAnalytics.totalScansLast12Months || 0,
            totalCustomersFromScans: storeAnalytics.totalCustomersFromScans || 0,
            storeId: storeAnalytics.storeId,
            companyCode: storeAnalytics.companyCode
        };
    }, [selectedStore, analyticsReport]);

    // Main analytics data from aggregated report
    const analytics = useMemo(() => {
        if (!analyticsReport || !analyticsReport.visits) {
            return null;
        }
        const report = analyticsReport.visits;

            // Format segments over time with month labels
            const segmentsOverTime = (report.segmentsOverTime || []).map(item => ({
                ...item,
                monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
            }));

            // Format cumulative growth with month labels
            const cumulativeGrowth = (report.cumulativeGrowth || []).map(item => ({
                ...item,
                monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
            }));

            // Format visit histogram
            const visitHistogram = Object.entries(report.visitCountDistribution || {})
                .map(([visits, count]) => ({ visits, count }))
                .sort((a, b) => {
                    if (a.visits === '10+') return 1;
                    if (b.visits === '10+') return -1;
                    return parseInt(a.visits) - parseInt(b.visits);
                });

            // Format top customers
            const topCustomers = (report.topByValue || []).slice(0, 10).map((v, index) => ({
                rank: index + 1,
                uid: v.uid || 'N/A',
                name: v.firstName || 'N/A',
                value: v.value || 0,
                visits: v.count || 0,
                lastVisit: v.lastVisit ? Moment(v.lastVisit).format('DD.MM.YYYY') : 'N/A'
            }));

            const topByVisits = (report.topByVisits || []).slice(0, 10).map((v, index) => ({
                rank: index + 1,
                uid: v.uid || 'N/A',
                name: v.firstName || 'N/A',
                value: v.value || 0,
                visits: v.count || 0,
                lastVisit: v.lastVisit ? Moment(v.lastVisit).format('DD.MM.YYYY') : 'N/A'
            }));

            // Segment distribution for pie chart
            const segments = report.segments || { novi: 0, vracajuci: 0, lojalni: 0 };
            const segmentDistribution = [
                { name: 'Novi kupci (1 poseta)', value: segments.novi, key: 'novi' },
                { name: 'Vraćajući (2-4 posete)', value: segments.vracajuci, key: 'vracajuci' },
                { name: 'Lojalni (5+ poseta)', value: segments.lojalni, key: 'lojalni' },
            ];

            // Value by segment
            const segmentValues = report.segmentValues || { novi: 0, vracajuci: 0, lojalni: 0 };
            const valueBySegment = [
                {
                    name: 'Novi',
                    value: segmentValues.novi,
                    count: segments.novi,
                    avgValue: segments.novi > 0 ? segmentValues.novi / segments.novi : 0
                },
                {
                    name: 'Vraćajući',
                    value: segmentValues.vracajuci,
                    count: segments.vracajuci,
                    avgValue: segments.vracajuci > 0 ? segmentValues.vracajuci / segments.vracajuci : 0
                },
                {
                    name: 'Lojalni',
                    value: segmentValues.lojalni,
                    count: segments.lojalni,
                    avgValue: segments.lojalni > 0 ? segmentValues.lojalni / segments.lojalni : 0
                },
            ];

            // Activity trend
            const summary = report.summary || {};
            const activityTrend = [
                { name: 'Poslednjih 30 dana', active: summary.activeInLast30 || 0 },
                { name: 'Poslednjih 90 dana', active: summary.activeInLast90 || 0 },
                { name: 'Poslednjih 365 dana', active: summary.activeInLast365 || 0 },
            ];

            // Customer acquisition from scans (real program growth)
            const scansData = analyticsReport.scans || {};
            const customerAcquisitionGrowth = (scansData.cumulativeCustomerGrowth || []).map(item => ({
                ...item,
                monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
            }));

            return {
                segments: {
                    novi: segments.novi,
                    vracajuci: segments.vracajuci,
                    lojalni: segments.lojalni,
                    neaktivni: summary.inactiveCount || 0
                },
                segmentValues,
                segmentDistribution,
                valueBySegment,
                activityTrend,
                visitHistogram,
                topCustomers,
                topByVisits,
                segmentsOverTime,
                cumulativeGrowth,
                customerAcquisitionGrowth,
                totalScansAllTime: scansData.totalScansAllTime || 0,
                totalCustomersFromScans: scansData.totalCustomersFromScans || 0,
                summary: {
                    totalCustomers: summary.totalCustomers || 0,
                    totalValue: summary.totalValue || 0,
                    totalVisits: summary.totalVisits || 0,
                    avgVisitsPerCustomer: summary.avgVisitsPerCustomer || 0,
                    avgValuePerCustomer: summary.avgValuePerCustomer || 0,
                    retentionRate: summary.retentionRate || 0,
                    loyaltyRate: summary.loyaltyRate || 0,
                    activeInLast30: summary.activeInLast30 || 0,
                    inactiveCount: summary.inactiveCount || 0
                },
                isAggregated: true
            };
    }, [analyticsReport]);

    // Scans data
    const scansData = useMemo(() => {
        if (analyticsReport && analyticsReport.scans) {
            return {
                monthlyActivity: (analyticsReport.scans.monthlyActivity || []).map(item => ({
                    ...item,
                    monthLabel: Moment(item.month, 'YYYY-MM').format('MMM YY')
                })),
                dailyActivity: analyticsReport.scans.dailyActivity || [],
                totalScans: analyticsReport.scans.totalScansLast12Months || 0
            };
        }
        return null;
    }, [analyticsReport]);

    // Custom label renderer for pie chart
    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return percent > 0.05 ? (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12}>
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        ) : null;
    };

    // If no aggregated data, show not available message
    if (!hasAggregatedData || !analytics) {
        return (
            <div className="space-y-6">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Loyalty Analytics</h3>
                    <p className="text-gray-600">
                        Ovaj izveštaj trenutno nije dostupan za vašu kompaniju.
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                        Kontaktirajte <span className="font-medium">Digitalnu Karticu</span> za aktivaciju ove funkcionalnosti.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Data Source Indicator */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-blue-600 font-medium">Agregirani podaci</span>
                    <span className="text-blue-500 text-sm">
                        (ažurirano: {analyticsReport.generatedAt ?
                            Moment(analyticsReport.generatedAt.toDate ? analyticsReport.generatedAt.toDate() : analyticsReport.generatedAt)
                                .format('DD.MM.YYYY HH:mm') : 'N/A'})
                    </span>
                </div>
                <span className="text-xs text-blue-400">Uključuje podatke iz scans kolekcije</span>
            </div>

            {/* Summary Cards - Always shown */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Ukupno kupaca</div>
                    <div className="text-2xl font-bold text-gray-900">
                        <NumberFormat value={analytics.summary.totalCustomers} displayType="text" thousandSeparator={true} />
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Ukupna vrednost</div>
                    <div className="text-2xl font-bold text-gray-900">
                        <NumberFormat value={Math.round(analytics.summary.totalValue)} displayType="text" thousandSeparator={true} />
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Prosek poseta</div>
                    <div className="text-2xl font-bold text-gray-900">
                        {analytics.summary.avgVisitsPerCustomer.toFixed(1)}
                    </div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Retention Rate</div>
                    <div className="text-2xl font-bold text-green-600">
                        {analytics.summary.retentionRate.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-400">2+ posete</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Loyalty Rate</div>
                    <div className="text-2xl font-bold text-purple-600">
                        {analytics.summary.loyaltyRate.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-400">5+ poseta</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow border">
                    <div className="text-sm text-gray-500">Aktivni (30 dana)</div>
                    <div className="text-2xl font-bold text-blue-600">
                        <NumberFormat value={analytics.summary.activeInLast30} displayType="text" thousandSeparator={true} />
                    </div>
                </div>
            </div>

            {/* Scans Activity Charts - Only shown when aggregated data is available */}
            {scansData && scansData.monthlyActivity && scansData.monthlyActivity.length > 0 && (
                <div className="grid grid-cols-1 gap-6">
                    <div className="bg-white p-4 rounded-lg shadow border">
                        <h3 className="text-lg font-semibold mb-2">Mesečna aktivnost (kupovine)</h3>
                        <p className="text-sm text-gray-500 mb-4">Stvarni broj kupovina i jedinstvenih kupaca po mesecu</p>
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={scansData.monthlyActivity}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="monthLabel" />
                                <YAxis yAxisId="left" orientation="left" />
                                <YAxis yAxisId="right" orientation="right" />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                    formatter={(value, name) => {
                                        const labels = {
                                            scansCount: 'Broj kupovina',
                                            uniqueCustomers: 'Jedinstvenih kupaca',
                                            totalValue: 'Ukupna vrednost'
                                        };
                                        if (name === 'totalValue') {
                                            return [value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), labels[name]];
                                        }
                                        return [value.toLocaleString('de-DE'), labels[name] || name];
                                    }}
                                />
                                <Legend
                                    formatter={(value) => {
                                        const labels = {
                                            scansCount: 'Broj kupovina',
                                            uniqueCustomers: 'Jedinstvenih kupaca'
                                        };
                                        return labels[value] || value;
                                    }}
                                />
                                <Bar yAxisId="left" dataKey="scansCount" name="scansCount" fill="#6366f1" radius={[4, 4, 0, 0]} />
                                <Bar yAxisId="left" dataKey="uniqueCustomers" name="uniqueCustomers" fill="#10b981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow border">
                        <h3 className="text-lg font-semibold mb-2">Mesečna vrednost kupovina</h3>
                        <p className="text-sm text-gray-500 mb-4">Ukupna vrednost svih kupovina po mesecu</p>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={scansData.monthlyActivity}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="monthLabel" />
                                <YAxis tickFormatter={(value) => value.toLocaleString('de-DE')} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                    formatter={(value) => [value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Vrednost']}
                                />
                                <Area type="monotone" dataKey="totalValue" stroke="#8b5cf6" fill="#c4b5fd" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Segments Over Time */}
            <div className="grid grid-cols-1 gap-6">
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Segmenti kroz vreme (poslednjih 12 meseci)</h3>
                    <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={analytics.segmentsOverTime}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="monthLabel" />
                            <YAxis />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                formatter={(value, name) => {
                                    const labels = { novi: 'Novi kupci', vracajuci: 'Vraćajući', lojalni: 'Lojalni' };
                                    return [value, labels[name] || name];
                                }}
                            />
                            <Legend
                                formatter={(value) => {
                                    const labels = { novi: 'Novi kupci (1 poseta)', vracajuci: 'Vraćajući (2-4)', lojalni: 'Lojalni (5+)' };
                                    return labels[value] || value;
                                }}
                            />
                            <Bar dataKey="novi" name="novi" stackId="a" fill={COLORS.novi} />
                            <Bar dataKey="vracajuci" name="vracajuci" stackId="a" fill={COLORS.vracajuci} />
                            <Bar dataKey="lojalni" name="lojalni" stackId="a" fill={COLORS.lojalni} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">
                        Rast programa lojalnosti
                        {analytics.totalCustomersFromScans > 0 && (
                            <span className="text-sm font-normal text-gray-500 ml-2">
                                (ukupno {analytics.totalCustomersFromScans.toLocaleString('de-DE')} kupaca iz {analytics.totalScansAllTime.toLocaleString('de-DE')} skeniranja)
                            </span>
                        )}
                    </h3>
                    {analytics.customerAcquisitionGrowth && analytics.customerAcquisitionGrowth.length > 0 ? (
                        <ResponsiveContainer width="100%" height={350}>
                            <AreaChart data={analytics.customerAcquisitionGrowth}>
                                <defs>
                                    <linearGradient id="colorNewCustomers" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.novi} stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor={COLORS.novi} stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.lojalni} stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor={COLORS.lojalni} stopOpacity={0.1}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="monthLabel"
                                    tick={{ fontSize: 11 }}
                                    interval={Math.ceil(analytics.customerAcquisitionGrowth.length / 12) - 1}
                                />
                                <YAxis yAxisId="left" orientation="left" stroke={COLORS.novi} />
                                <YAxis yAxisId="right" orientation="right" stroke={COLORS.lojalni} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                    formatter={(value, name) => {
                                        const labels = { newCustomers: 'Novi kupci (mesec)', totalCustomers: 'Ukupno kupaca' };
                                        return [value.toLocaleString('de-DE'), labels[name] || name];
                                    }}
                                />
                                <Legend
                                    formatter={(value) => {
                                        const labels = { newCustomers: 'Novi kupci (mesečno)', totalCustomers: 'Ukupno kupaca (kumulativno)' };
                                        return labels[value] || value;
                                    }}
                                />
                                <Area
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="newCustomers"
                                    name="newCustomers"
                                    stroke={COLORS.novi}
                                    fillOpacity={1}
                                    fill="url(#colorNewCustomers)"
                                />
                                <Line
                                    yAxisId="right"
                                    type="monotone"
                                    dataKey="totalCustomers"
                                    name="totalCustomers"
                                    stroke={COLORS.lojalni}
                                    strokeWidth={3}
                                    dot={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-64 text-gray-500">
                            <p>Podaci o akviziciji kupaca nisu dostupni. Pokrenite agregaciju za prikaz.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Charts Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Segment Distribution Pie Chart */}
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Distribucija segmenata</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={analytics.segmentDistribution}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={renderCustomLabel}
                                outerRadius={100}
                                dataKey="value"
                            >
                                {analytics.segmentDistribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Kupaca']} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Value by Segment */}
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Vrednost po segmentima</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analytics.valueBySegment}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis tickFormatter={(value) => value.toLocaleString('de-DE')} />
                            <Tooltip
                                formatter={(value, name) => {
                                    const formattedValue = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                    if (name === 'value') return [formattedValue, 'Ukupna vrednost'];
                                    if (name === 'avgValue') return [formattedValue, 'Prosečna vrednost'];
                                    return [formattedValue, name];
                                }}
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                            />
                            <Legend />
                            <Bar dataKey="value" name="Ukupna vrednost" fill="#6366f1" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="avgValue" name="Prosečna vrednost" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Visit Count Distribution */}
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Distribucija broja poseta</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analytics.visitHistogram}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="visits" label={{ value: 'Broj poseta', position: 'bottom', offset: -5 }} />
                            <YAxis label={{ value: 'Broj kupaca', angle: -90, position: 'insideLeft' }} />
                            <Tooltip formatter={(value) => [value, 'Kupaca']} />
                            <Bar dataKey="count" fill="#8b5cf6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Activity Trend */}
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Aktivnost kupaca</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analytics.activityTrend} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={120} />
                            <Tooltip formatter={(value) => [value, 'Aktivnih kupaca']} />
                            <Bar dataKey="active" fill="#3b82f6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Top Customers Tables */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top by Value */}
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Top 10 kupaca po vrednosti</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left">#</th>
                                    <th className="px-3 py-2 text-left">UID</th>
                                    <th className="px-3 py-2 text-left">Ime</th>
                                    <th className="px-3 py-2 text-right">Vrednost</th>
                                    <th className="px-3 py-2 text-right">Poseta</th>
                                    <th className="px-3 py-2 text-left">Poslednja</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analytics.topCustomers.map((customer, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-3 py-2">{customer.rank}</td>
                                        <td className="px-3 py-2 text-xs text-gray-500 font-mono">{customer.uid}</td>
                                        <td className="px-3 py-2 font-medium">{customer.name}</td>
                                        <td className="px-3 py-2 text-right">
                                            <NumberFormat value={Math.round(customer.value)} displayType="text" thousandSeparator={true} />
                                        </td>
                                        <td className="px-3 py-2 text-right">{customer.visits}</td>
                                        <td className="px-3 py-2 text-gray-500">{customer.lastVisit}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Top by Visits */}
                <div className="bg-white p-4 rounded-lg shadow border">
                    <h3 className="text-lg font-semibold mb-4">Top 10 kupaca po broju poseta</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left">#</th>
                                    <th className="px-3 py-2 text-left">UID</th>
                                    <th className="px-3 py-2 text-left">Ime</th>
                                    <th className="px-3 py-2 text-right">Poseta</th>
                                    <th className="px-3 py-2 text-right">Vrednost</th>
                                    <th className="px-3 py-2 text-left">Poslednja</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analytics.topByVisits.map((customer, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="px-3 py-2">{customer.rank}</td>
                                        <td className="px-3 py-2 text-xs text-gray-500 font-mono">{customer.uid}</td>
                                        <td className="px-3 py-2 font-medium">{customer.name}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-purple-600">{customer.visits}</td>
                                        <td className="px-3 py-2 text-right">
                                            <NumberFormat value={Math.round(customer.value)} displayType="text" thousandSeparator={true} />
                                        </td>
                                        <td className="px-3 py-2 text-gray-500">{customer.lastVisit}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Segment Details Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-green-800 font-semibold">Novi kupci</div>
                            <div className="text-sm text-green-600">1 poseta</div>
                        </div>
                        <div className="text-3xl font-bold text-green-700">{analytics.segments.novi}</div>
                    </div>
                    <div className="mt-2 text-sm text-green-600">
                        Vrednost: <NumberFormat value={Math.round(analytics.segmentValues.novi)} displayType="text" thousandSeparator={true} />
                    </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-blue-800 font-semibold">Vraćajući</div>
                            <div className="text-sm text-blue-600">2-4 posete</div>
                        </div>
                        <div className="text-3xl font-bold text-blue-700">{analytics.segments.vracajuci}</div>
                    </div>
                    <div className="mt-2 text-sm text-blue-600">
                        Vrednost: <NumberFormat value={Math.round(analytics.segmentValues.vracajuci)} displayType="text" thousandSeparator={true} />
                    </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-purple-800 font-semibold">Lojalni</div>
                            <div className="text-sm text-purple-600">5+ poseta</div>
                        </div>
                        <div className="text-3xl font-bold text-purple-700">{analytics.segments.lojalni}</div>
                    </div>
                    <div className="mt-2 text-sm text-purple-600">
                        Vrednost: <NumberFormat value={Math.round(analytics.segmentValues.lojalni)} displayType="text" thousandSeparator={true} />
                    </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-orange-800 font-semibold">Neaktivni</div>
                            <div className="text-sm text-orange-600">30+ dana</div>
                        </div>
                        <div className="text-3xl font-bold text-orange-700">{analytics.segments.neaktivni}</div>
                    </div>
                    <div className="mt-2 text-sm text-orange-600">
                        Poslednja poseta pre 30+ dana
                    </div>
                </div>
            </div>

            {/* Store-Level Analytics Section - Only show if stores exist */}
            {storeList.length > 0 && (
                <div className="space-y-6 mt-8 pt-8 border-t-2 border-gray-200">
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <h2 className="text-xl font-bold text-indigo-800 mb-2">Analitika po prodavnicama</h2>
                        <p className="text-sm text-indigo-600">Detaljni prikaz rasta programa lojalnosti za svaku prodavnicu pojedinačno</p>
                    </div>

                    {/* Store Selector */}
                    <div className="bg-white p-4 rounded-lg shadow border">
                        <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700">Izaberite prodavnicu:</label>
                            <select
                                value={selectedStore}
                                onChange={(e) => setSelectedStore(e.target.value)}
                                className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                                <option value="all">-- Izaberite prodavnicu --</option>
                                {storeList.map(store => (
                                    <option key={store.key} value={store.key}>
                                        {store.label} ({store.totalCustomers.toLocaleString('de-DE')} kupaca, {store.totalScans.toLocaleString('de-DE')} skeniranja)
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Store-Specific Charts - Show when a specific store is selected */}
                    {selectedStore !== 'all' && selectedStoreData && (
                        <div className="space-y-6">
                            {/* Store Info Banner */}
                            <div className="bg-white p-4 rounded-lg shadow border border-indigo-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-lg font-semibold text-indigo-800">
                                            {selectedStoreData.storeId} - {selectedStoreData.companyCode}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Ukupno <strong>{selectedStoreData.totalCustomersFromScans.toLocaleString('de-DE')}</strong> kupaca iz <strong>{selectedStoreData.totalScansAllTime.toLocaleString('de-DE')}</strong> skeniranja
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Store Monthly Activity Chart */}
                            {selectedStoreData.monthlyActivity && selectedStoreData.monthlyActivity.length > 0 && (
                                <div className="bg-white p-4 rounded-lg shadow border">
                                    <h3 className="text-lg font-semibold mb-2">Mesečna aktivnost - {selectedStoreData.storeId}</h3>
                                    <p className="text-sm text-gray-500 mb-4">Broj kupovina i jedinstvenih kupaca po mesecu za ovu prodavnicu</p>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <BarChart data={selectedStoreData.monthlyActivity}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="monthLabel" />
                                            <YAxis yAxisId="left" orientation="left" />
                                            <YAxis yAxisId="right" orientation="right" />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                                formatter={(value, name) => {
                                                    const labels = {
                                                        scansCount: 'Broj kupovina',
                                                        uniqueCustomers: 'Jedinstvenih kupaca',
                                                        totalValue: 'Ukupna vrednost'
                                                    };
                                                    if (name === 'totalValue') {
                                                        return [value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), labels[name]];
                                                    }
                                                    return [value.toLocaleString('de-DE'), labels[name] || name];
                                                }}
                                            />
                                            <Legend
                                                formatter={(value) => {
                                                    const labels = {
                                                        scansCount: 'Broj kupovina',
                                                        uniqueCustomers: 'Jedinstvenih kupaca'
                                                    };
                                                    return labels[value] || value;
                                                }}
                                            />
                                            <Bar yAxisId="left" dataKey="scansCount" name="scansCount" fill="#6366f1" radius={[4, 4, 0, 0]} />
                                            <Bar yAxisId="left" dataKey="uniqueCustomers" name="uniqueCustomers" fill="#10b981" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Store Value Chart */}
                            {selectedStoreData.monthlyActivity && selectedStoreData.monthlyActivity.length > 0 && (
                                <div className="bg-white p-4 rounded-lg shadow border">
                                    <h3 className="text-lg font-semibold mb-2">Mesečna vrednost - {selectedStoreData.storeId}</h3>
                                    <p className="text-sm text-gray-500 mb-4">Ukupna vrednost kupovina po mesecu za ovu prodavnicu</p>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart data={selectedStoreData.monthlyActivity}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="monthLabel" />
                                            <YAxis tickFormatter={(value) => value.toLocaleString('de-DE')} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                                formatter={(value) => [value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Vrednost']}
                                            />
                                            <Area type="monotone" dataKey="totalValue" stroke="#8b5cf6" fill="#c4b5fd" strokeWidth={2} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Store Customer Growth Chart */}
                            {selectedStoreData.cumulativeCustomerGrowth && selectedStoreData.cumulativeCustomerGrowth.length > 0 && (
                                <div className="bg-white p-4 rounded-lg shadow border">
                                    <h3 className="text-lg font-semibold mb-2">Rast programa lojalnosti - {selectedStoreData.storeId}</h3>
                                    <p className="text-sm text-gray-500 mb-4">Novi kupci i kumulativni rast za ovu prodavnicu</p>
                                    <ResponsiveContainer width="100%" height={350}>
                                        <AreaChart data={selectedStoreData.cumulativeCustomerGrowth}>
                                            <defs>
                                                <linearGradient id="colorNewCustomersStore" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={COLORS.novi} stopOpacity={0.8}/>
                                                    <stop offset="95%" stopColor={COLORS.novi} stopOpacity={0.1}/>
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="monthLabel"
                                                tick={{ fontSize: 11 }}
                                                interval={Math.ceil(selectedStoreData.cumulativeCustomerGrowth.length / 12) - 1}
                                            />
                                            <YAxis yAxisId="left" orientation="left" stroke={COLORS.novi} />
                                            <YAxis yAxisId="right" orientation="right" stroke={COLORS.lojalni} />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}
                                                formatter={(value, name) => {
                                                    const labels = { newCustomers: 'Novi kupci (mesec)', totalCustomers: 'Ukupno kupaca' };
                                                    return [value.toLocaleString('de-DE'), labels[name] || name];
                                                }}
                                            />
                                            <Legend
                                                formatter={(value) => {
                                                    const labels = { newCustomers: 'Novi kupci (mesečno)', totalCustomers: 'Ukupno kupaca (kumulativno)' };
                                                    return labels[value] || value;
                                                }}
                                            />
                                            <Area
                                                yAxisId="left"
                                                type="monotone"
                                                dataKey="newCustomers"
                                                name="newCustomers"
                                                stroke={COLORS.novi}
                                                fillOpacity={1}
                                                fill="url(#colorNewCustomersStore)"
                                            />
                                            <Line
                                                yAxisId="right"
                                                type="monotone"
                                                dataKey="totalCustomers"
                                                name="totalCustomers"
                                                stroke={COLORS.lojalni}
                                                strokeWidth={3}
                                                dot={false}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
